'use client';

import type { MouseEventHandler } from 'react';
import { Suspense } from 'react';
import cx from 'clsx';
import Link from 'next/link';
import { Badge } from '@kcd/design-system';

import {
  useShoppingCartCount,
  useShoppingCartPrice,
} from '~/domains/cart/queries';

import { PointLogo24x24Icon } from '~/assets/icons';
import LoadingSpinnerIcon from '~/assets/icons/scb_loading_spinner.svg';

type Props = {
  className?: string;
  /** 클릭 트래킹 핸들러 (TrackClick 등 호출부에서 주입) */
  onClick?: MouseEventHandler<HTMLElement>;
};

type StickyCartBarContentProps = {
  isFetching: boolean;
  totalAmount: number;
  discountedTotalAmount?: number;
};

function StickyCartBarContent({
  isFetching,
  totalAmount,
  discountedTotalAmount,
}: StickyCartBarContentProps) {
  return discountedTotalAmount ? (
    <>
      <div className="flex items-end gap-4 text-gray-000">
        <span className="shrink-0 typo-paragraph100-regular disable-text-size-adjust">
          나의할인가
        </span>
        <span className="truncate typo-heading100">
          {!isFetching ? (
            '계산중...'
          ) : (
            <>{discountedTotalAmount?.toLocaleString()}원</>
          )}
        </span>
      </div>
      <div className="flex items-center gap-4 text-gray-000 opacity-70 typo-caption100-regular disable-text-size-adjust">
        <span className="shrink-0">총 금액</span>
        <span className="truncate">
          {!isFetching ? '계산중...' : <>{totalAmount?.toLocaleString()}원</>}
        </span>
      </div>
    </>
  ) : (
    <div className="flex items-end gap-6 text-gray-000">
      <span className="shrink-0 typo-paragraph100-regular disable-text-size-adjust">
        총 금액
      </span>
      <span className="truncate typo-heading100">
        {!isFetching ? '계산중...' : <>{totalAmount?.toLocaleString()}원</>}
      </span>
    </div>
  );
}

function StickyCartBarInner({ className, onClick }: Props) {
  const { count, isFetching: isFetchingCartCount } = useShoppingCartCount();
  const { data, isFetching: isFetchingCart } = useShoppingCartPrice('NONE');

  if (!data || data?.totalAmount === 0) return null;

  const isAllFetching = !isFetchingCartCount && !isFetchingCart;

  return (
    <Link href="/cart" className="w-full">
      <button className="flex w-full flex-col" type="button" onClick={onClick}>
        <div
          className={cx([
            'flex items-center py-12 px-20 rounded-tr-16 rounded-tl-16 w-full bg-[#222b3ef2]',
            className,
          ])}
        >
          <div className="flex flex-1 flex-col self-center">
            <StickyCartBarContent
              isFetching
              totalAmount={data.totalAmount}
              discountedTotalAmount={data.discountedTotalAmount}
            />
          </div>
          <div className="relative my-[1px] ml-12 flex h-40 w-80 shrink-0 items-center justify-center rounded-12 border-gray-300 bg-gray-000 text-gray-1400 typo-paragraph300-semibold border-1">
            {!isAllFetching ? (
              <div className="size-[30px]">
                <LoadingSpinnerIcon />
              </div>
            ) : (
              <>
                주문하기
                <Badge
                  type="number"
                  text={count}
                  className="absolute right-[1px] top-2 -translate-y-1/2 translate-x-1/2"
                />
              </>
            )}
          </div>
        </div>
        {!!data.expectedPoints && (
          <div className="flex w-full items-center gap-4 bg-primary-air px-20 py-4">
            <PointLogo24x24Icon />
            <p className="typo-paragraph100-semibold">
              총{' '}
              <span className="text-accent">
                {data.expectedPoints?.toLocaleString()}
              </span>{' '}
              포인트 적립 가능
            </p>
          </div>
        )}
      </button>
    </Link>
  );
}

/**
 * 하단 고정 장바구니 바
 * Pages 의 `~/components/StickyCartBar` 에서 next/router → next/navigation 으로 전환.
 *
 * 장바구니 카운트/가격 훅이 suspense 라 호출부가 별도 Suspense 로 감싸야 했던 것을,
 * 컴포넌트가 자체 Suspense 경계를 내장하도록 하여 호출부는 그냥 렌더만 하면 되게 한다.
 */
export function StickyCartBar(props: Props) {
  return (
    <Suspense fallback={null}>
      <StickyCartBarInner {...props} />
    </Suspense>
  );
}
